/**
 * Practice details: the Xero sales account and tax rate the practice invoices with by default.
 *
 * Lens dispensing invoices use them on every line, and the invoice and claim dialogs start new lines on
 * them. Chosen from the organisation's own accounts and tax rates when the user is logged in to Xero for
 * this practice; otherwise typed as codes. Shown only with the practice's billingFeature.
 */
app.component("billingDefaults", {
	templateUrl: "app/src/js/components/billingDefaults/billingDefaults.html",
	controller: "billingDefaultsController",
	bindings: { practiceId: "<" },
});

app.controller("billingDefaultsController", [
	"$scope",
	"$window",
	"Session",
	"PracticeService",
	"BillingAPI",
	"PracticeBillingDefaults",
	function ($scope, $window, Session, PracticeService, BillingAPI, PracticeBillingDefaults) {
		const $ctrl = this;

		$scope.canEdit = false;
		$scope.state = { loading: true, error: null, saving: false, saved: false };
		// Unset is null, which the pickers' "Xero's default" option stands for.
		$scope.form = { salesAccountCode: null, taxType: null };
		$scope.fallback = PracticeBillingDefaults.fallback();
		// The organisation's accounts and tax rates, when the user is logged in to Xero for this practice.
		$scope.xero = { checked: false, accounts: [], taxRates: [] };
		let saved = "";

		const snapshot = () => angular.toJson($scope.form);
		$scope.isDirty = () => snapshot() !== saved;

		$ctrl.$onChanges = function () {
			$scope.practiceId = $ctrl.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find((p) => p.id === $scope.practiceId) || Session.practice || {};
			$scope.canEdit = Session.hasPermissionAt(practice, "BILLING_DEFAULTS_MANAGE");
			const features = practice.practiceFeatures
				|| (Session.practice && Session.practice.id === $scope.practiceId ? Session.practice.practiceFeatures : null) || {};
			$scope.enabled = !!features.billingFeature;
			if (!$scope.enabled) return;
			load();
			loadXero();
		};

		function adopt(data) {
			$scope.form = { salesAccountCode: (data && data.sales_account_code) || null, taxType: (data && data.tax_type) || null };
			saved = snapshot();
		}

		function load() {
			$scope.state.loading = true;
			$scope.state.error = null;
			PracticeService.queryBillingDefaults($scope.practiceId)
				.then((resp) => adopt(resp.data))
				.catch(() => ($scope.state.error = "The billing defaults could not be loaded."))
				.finally(() => ($scope.state.loading = false));
		}
		$scope.reload = load;

		// Xero speaks for the practice the user is working in, so its lists are offered only there.
		function loadXero() {
			$scope.xero.checked = false;
			if (!Session.practice || Session.practice.id !== $scope.practiceId) {
				$scope.xero.checked = true;
				return;
			}
			BillingAPI.getLoggedIn()
				.then(function (resp) {
					const s = resp.data || {};
					const linked = !!s.logged_in_to && !s.error_organisation_conflict;
					$scope.xero.organisation = linked ? s.logged_in_to : null;
					$scope.xero.accounts = linked ? (s.accounts || []).filter((a) => a.account_class === "REVENUE") : [];
					$scope.xero.taxRates = linked ? s.tax_rates || [] : [];
				})
				.catch(() => ($scope.xero.accounts = $scope.xero.taxRates = []))
				.finally(() => ($scope.xero.checked = true));
		}
		$scope.checkXero = loadXero;

		$scope.loginXero = function () {
			BillingAPI.getXeroLoginUrl().then(function (resp) {
				$window.open(resp.data, "_blank", "location=no,toolbar=yes,scrollbars=yes,resizable=yes,top=200,left=500,width=500,height=600");
			});
		};

		$scope.hasLists = () => $scope.xero.accounts.length > 0 || $scope.xero.taxRates.length > 0;
		$scope.accountLabel = (a) => `${a.account_code} - ${a.account_name}`;
		$scope.taxLabel = (t) => `${t.tax_name}${t.tax_display_rate !== undefined && t.tax_display_rate !== null ? " (" + t.tax_display_rate + "%)" : ""}`;

		// A saved code the organisation doesn't list (archived, or typed before connecting) stays choosable.
		// Kept between changes: ng-options watches the list, and a new one on every look never settles.
		$scope.accountChoices = [];
		$scope.taxChoices = [];
		function refreshChoices() {
			const accounts = $scope.xero.accounts;
			const code = $scope.form.salesAccountCode;
			$scope.accountChoices = code && !accounts.some((a) => a.account_code === code)
				? accounts.concat([{ account_code: code, account_name: "not in this organisation" }]) : accounts;
			const rates = $scope.xero.taxRates;
			const type = $scope.form.taxType;
			$scope.taxChoices = type && !rates.some((t) => t.tax_type === type)
				? rates.concat([{ tax_type: type, tax_name: type + " - not in this organisation" }]) : rates;
		}
		$scope.$watchGroup(["xero.accounts", "xero.taxRates", "form.salesAccountCode", "form.taxType"], refreshChoices);

		$scope.$watch($scope.isDirty, (dirty) => dirty && ($scope.state.saved = false));

		$scope.undo = function () {
			$scope.form = angular.fromJson(saved);
		};

		$scope.save = function () {
			if (!$scope.canEdit || !$scope.isDirty() || $scope.state.saving) return;
			$scope.state.saving = true;
			$scope.state.error = null;
			PracticeService.updateBillingDefaults({ sales_account_code: $scope.form.salesAccountCode || null, tax_type: $scope.form.taxType || null }, $scope.practiceId)
				.then(function (resp) {
					adopt(resp.data);
					PracticeBillingDefaults.saved($scope.practiceId, resp.data);
					$scope.state.saved = true;
				})
				.catch((resp) => ($scope.state.error = (resp && resp.data && resp.data.message) || "The billing defaults could not be saved."))
				.finally(() => ($scope.state.saving = false));
		};
	},
]);
